import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiCheckerSettingName,
  ApiCheckerSettings,
  getApiCheckerSettings,
  updateApiCheckerSetting,
} from "api/ApiKeySettings";
import { isUserApiKey } from "utils/auth/apiKeyStore";

/**
 * An api key's checker settings, loaded once per key, with optimistic saves.
 *
 * `update` shows the new value at once and sends it; on any failure (400
 * envelope, 5xx, network) that one setting snaps back to its previous value
 * and the call resolves `false`, so the caller shows an error — a failed save
 * never looks like a success (spec 2026-10-20-website-api-settings, US1/US2).
 *
 * `confirmed` is the row as the server last stored it: the load, then each
 * save the server accepted. It never carries an optimistic value, so anything
 * that acts on a setting (the checker page's paid `/check-ip` lookup) reads
 * `confirmed`; a failed save leaves it untouched and triggers nothing.
 *
 * A save belongs to the key it was sent for: when the key changes while it is
 * in flight (a rotation on /profile/api), its answer, success or failure, is
 * dropped, never written into the new key's row (`isCurrentKey`).
 *
 * Not built on `useApiRequest`: that hook keeps the server's answer as
 * read-only data, while these settings are edited in place and rolled back.
 */
export const useApiCheckerSettings = (apiKey: string | null | undefined) => {
  const [settings, setSettings] = useState<ApiCheckerSettings | null>(null);
  const [confirmed, setConfirmed] = useState<ApiCheckerSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [pending, setPending] = useState<Partial<Record<ApiCheckerSettingName, boolean>>>(
    {}
  );
  const settingsRef = useRef<ApiCheckerSettings | null>(null);
  settingsRef.current = settings;
  // The key this render shows; a save compares its own key against it.
  const currentKeyRef = useRef(apiKey);
  currentKeyRef.current = apiKey;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Saves in flight belong to the previous key: their markers go with it.
    setPending({});
    if (!isUserApiKey(apiKey)) {
      setSettings(null);
      setConfirmed(null);
      setLoading(false);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getApiCheckerSettings(apiKey)
      .then((row) => {
        if (!cancelled) {
          setSettings(row);
          setConfirmed(row);
        }
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  const update = useCallback(
    async (checker: ApiCheckerSettingName, value: number): Promise<boolean> => {
      if (!isUserApiKey(apiKey) || !settingsRef.current) return false;
      const previous = settingsRef.current[checker];
      const apply = (next: unknown, onlyIf?: (current: unknown) => boolean) =>
        setSettings((current) =>
          current && (!onlyIf || onlyIf(current[checker]))
            ? { ...current, [checker]: next }
            : current
        );

      // The one predicate for "may this save still touch state".
      const isCurrentKey = () => mounted.current && currentKeyRef.current === apiKey;

      apply(value);
      setPending((p) => ({ ...p, [checker]: true }));
      try {
        const row = await updateApiCheckerSetting(apiKey, checker, value);
        if (isCurrentKey()) {
          const raw = row?.[checker];
          const stored = raw === undefined || raw === null ? value : raw;
          apply(stored);
          setConfirmed((current) => (current ? { ...current, [checker]: stored } : current));
        }
        return true;
      } catch {
        // Roll back only what this call changed.
        if (isCurrentKey()) apply(previous, (current) => current === value);
        return false;
      } finally {
        if (isCurrentKey()) {
          setPending((p) => {
            const next = { ...p };
            delete next[checker];
            return next;
          });
        }
      }
    },
    [apiKey]
  );

  return { settings, confirmed, loading, loadError, pending, update };
};
